import { Injectable } from '@angular/core';
import { fromEvent, merge, timer, Subscription, take } from 'rxjs';
import { Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { AutoLogoutComponent } from 'src/app/auto-logout/auto-logout.component';
import { TokenService } from 'src/app/_shared/services/token.service';
import { DiligentApiService } from 'src/app/_shared/services/diligent-api.service';
import { NotificationsService } from 'src/app/_shared/modules/notifications/shared/notifications.service';

@Injectable({ providedIn: 'root' })
export class InactivityService {
  private activityEvents = ['mousemove', 'keypress', 'touchstart'];
  private activitySub?: Subscription;
  private warningTimerSub?: Subscription;
  private logoutTimerSub?: Subscription;

  private warningTime = 23 * 60 * 60 * 1000; // 23 hours
  private logoutTime = 24 * 60 * 60 * 1000; // 24 hours
  private activeDialog: MatDialogRef<AutoLogoutComponent>;
  private isRefreshing = false;

  constructor(
    private router: Router,
    private token: TokenService,
    private dialog: MatDialog,
    private diligentService: DiligentApiService,
    private notificationsService: NotificationsService
  ) {}

  startWatching() {
    const activity$ = merge(...this.activityEvents.map((event) => fromEvent(window, event)));

    this.activitySub = activity$.subscribe(() => this.resetTimers());

    this.resetTimers();
  }

  private resetTimers() {
    this.warningTimerSub?.unsubscribe();
    this.logoutTimerSub?.unsubscribe();

    this.warningTimerSub = timer(this.warningTime).subscribe(() => {
      this.activeDialog = this.dialog.open(AutoLogoutComponent, {
        data: {
          warn: true,
        },
      });
      this.activeDialog.afterClosed().subscribe((result) => {
        if (result) {
          this.resetTimers();
        } else if (result === false) {
          this.token.expire();
          this.router.navigate(['/login']);
        }
        this.activeDialog = null;
      });
    });

    this.logoutTimerSub = timer(this.logoutTime).subscribe(() => {
      this.activeDialog.close();
      this.token.expire();
      this.router.navigate(['/login']);
    });

    this.token
      .isExpired()
      .pipe(take(1))
      .subscribe((expired) => {
        if (expired === 'soon' && !this.isRefreshing) {
          this.isRefreshing = true;
          this.diligentService
            .renewTokenNew()
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.isRefreshing = false;
              },
              error: (error) => {
                this.notificationsService.alert(error.error.message, 'Error');
                this.activeDialog.close();
                this.token.expire();
                this.router.navigate(['/login']);
                this.isRefreshing = false;
              },
            });
        }
      });
  }

  stopWatching() {
    this.activitySub?.unsubscribe();
    this.warningTimerSub?.unsubscribe();
    this.logoutTimerSub?.unsubscribe();
  }
}
